import { useState } from "react";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "@/components/ui/select";
import { Upload, Send, FileSpreadsheet, Users, CheckCircle, AlertCircle, Clock } from "lucide-react";
import { toast } from "sonner";

interface BulkInvitesSectionProps {
  jobs: Array<{ id: string | number; title: string; status?: string }>;
  onBatchCreated: () => void;
}

interface CandidateEntry {
  name: string;
  email: string;
}

export function BulkInvitesSection({ jobs, onBatchCreated }: BulkInvitesSectionProps) {
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [questionCount, setQuestionCount] = useState(5);
  const [expiryDays, setExpiryDays] = useState(3);
  const [autoDispatch, setAutoDispatch] = useState(true);
  const [emailSubject, setEmailSubject] = useState("You're Invited to Interview");
  const [emailTemplate, setEmailTemplate] = useState(
    `Hello {{name}},\n\nYou have been invited to complete a video interview for a position we're recruiting for.\n\nClick the link below to begin:\n{{link}}\n\nThis link expires in {{expiry}} days.\n\nBest regards,\nVEXIS Talent Agency`
  );
  
  const [candidates, setCandidates] = useState<CandidateEntry[]>([]);
  const [csvContent, setCsvContent] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadMode, setUploadMode] = useState<"csv" | "manual">("manual");
  const [batchStatus, setBatchStatus] = useState<{
    total: number;
    sent: number;
    failed: number;
    batchId?: number;
  } | null>(null);

  const [manualName, setManualName] = useState("");
  const [manualEmail, setManualEmail] = useState("");

  const addManualCandidate = () => {
    if (!manualName.trim() || !manualEmail.trim()) {
      toast.error("Please enter both name and email");
      return;
    }
    if (!manualEmail.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    setCandidates([...candidates, { name: manualName.trim(), email: manualEmail.trim() }]);
    setManualName("");
    setManualEmail("");
  };

  const removeCandidate = (index: number) => {
    setCandidates(candidates.filter((_, i) => i !== index));
  };

  const parseCsv = (content: string) => {
    const lines = content.trim().split("\n");
    const parsed: CandidateEntry[] = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      const [name, email] = line.split(",").map(s => s.trim().replace(/^["']|["']$/g, ""));
      
      if (i === 0 && (name.toLowerCase() === "name" || email?.toLowerCase() === "email")) {
        continue;
      }
      
      if (name && email && email.includes("@")) {
        parsed.push({ name, email });
      }
    }
    
    return parsed;
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      setCsvContent(content);
      const parsed = parseCsv(content);
      setCandidates(parsed);
      toast.success(`Loaded ${parsed.length} candidates from CSV`);
    };
    reader.readAsText(file);
  };

  const handleSubmit = async () => {
    if (!selectedRoleId) {
      toast.error("Please select a role");
      return;
    }
    if (candidates.length === 0) {
      toast.error("Please add at least one candidate");
      return;
    }

    setIsProcessing(true);
    setBatchStatus(null);

    try {
      const response = await apiRequest("POST", "/api/bulk-invites/batches", {
          roleId: parseInt(selectedRoleId),
          candidates,
          questionCount,
          expiryDays,
          emailSubject,
          emailTemplate,
          dispatchMode: autoDispatch ? "auto" : "manual",
        });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || "Failed to create batch");
      }

      setBatchStatus({
        total: data.totalCandidates,
        sent: data.sentCount || 0,
        failed: data.failedCount || 0,
        batchId: data.batchId,
      });

      if (autoDispatch) {
        const dispatchRes = await apiRequest("POST", `/api/bulk-invites/batches/${data.batchId}/dispatch`);
        
        const dispatchData = await dispatchRes.json();
        
        setBatchStatus({
          total: dispatchData.totalCandidates,
          sent: dispatchData.sentCount,
          failed: dispatchData.failedCount,
          batchId: data.batchId,
        });

        toast.success(`Sent ${dispatchData.sentCount} invites successfully`);
      } else {
        toast.success(`Created batch with ${data.totalCandidates} candidates. Use manual dispatch to send emails.`);
      }

      setCandidates([]);
      setCsvContent("");
      onBatchCreated();
    } catch (error: any) {
      toast.error(error.message || "Failed to process bulk invites");
    } finally {
      setIsProcessing(false);
    }
  };

  const activeJobs = jobs.filter(j => {
    const status = (j.status || "").toLowerCase();
    return !["closed", "expired", "filled", "cancelled"].includes(status);
  });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-sm font-medium mb-2 block">Select Role</Label>
          <Select value={selectedRoleId} onValueChange={setSelectedRoleId}>
            <SelectTrigger data-testid="bulk-select-role">
              <SelectValue placeholder="Choose a role..." />
            </SelectTrigger>
            <SelectContent>
              {activeJobs.map((job) => (
                <SelectItem key={job.id} value={job.id.toString()}>
                  {job.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-sm font-medium mb-2 block">Questions</Label>
            <Select value={questionCount.toString()} onValueChange={(v) => setQuestionCount(parseInt(v))}>
              <SelectTrigger data-testid="bulk-question-count">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[3, 4, 5, 6, 7, 8, 10, 12].map(n => (
                  <SelectItem key={n} value={n.toString()}>{n} questions</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm font-medium mb-2 block">Expiry</Label>
            <Select value={expiryDays.toString()} onValueChange={(v) => setExpiryDays(parseInt(v))}>
              <SelectTrigger data-testid="bulk-expiry-days">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 day</SelectItem>
                <SelectItem value="3">3 days</SelectItem>
                <SelectItem value="7">7 days</SelectItem>
                <SelectItem value="14">14 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
        <div className="flex items-center gap-2">
          <Send className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium">Auto-dispatch emails</span>
        </div>
        <Switch
          checked={autoDispatch}
          onCheckedChange={setAutoDispatch}
          data-testid="bulk-auto-dispatch"
        />
      </div>

      <div className="border-t pt-4">
        <div className="flex gap-2 mb-4">
          <Button
            variant={uploadMode === "manual" ? "default" : "outline"}
            size="sm"
            onClick={() => setUploadMode("manual")}
            data-testid="bulk-mode-manual"
          >
            <Users className="w-4 h-4 mr-2" />
            Manual Entry
          </Button>
          <Button
            variant={uploadMode === "csv" ? "default" : "outline"}
            size="sm"
            onClick={() => setUploadMode("csv")}
            data-testid="bulk-mode-csv"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            CSV Upload
          </Button>
        </div>

        {uploadMode === "csv" && (
          <div className="space-y-4">
            <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center">
              <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground mb-2">
                Upload a CSV file with columns: name, email
              </p>
              <Input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="max-w-xs mx-auto"
                data-testid="bulk-csv-upload"
              />
            </div>
          </div>
        )}

        {uploadMode === "manual" && (
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input
                placeholder="Candidate name"
                value={manualName}
                onChange={(e) => setManualName(e.target.value)}
                className="flex-1"
                data-testid="bulk-manual-name"
              />
              <Input
                placeholder="Email address"
                type="email"
                value={manualEmail}
                onChange={(e) => setManualEmail(e.target.value)}
                className="flex-1"
                data-testid="bulk-manual-email"
              />
              <Button onClick={addManualCandidate} data-testid="bulk-add-candidate">
                Add
              </Button>
            </div>
          </div>
        )}

        {candidates.length > 0 && (
          <div className="mt-4 p-3 bg-muted/30 rounded-lg max-h-48 overflow-y-auto">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">{candidates.length} candidates added</span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setCandidates([])}
                className="text-xs text-destructive hover:text-destructive"
              >
                Clear all
              </Button>
            </div>
            <div className="space-y-1">
              {candidates.slice(0, 20).map((c, i) => (
                <div key={i} className="flex items-center justify-between text-sm py-1 px-2 bg-background rounded">
                  <span>{c.name}</span>
                  <span className="text-muted-foreground">{c.email}</span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 w-6 p-0"
                    onClick={() => removeCandidate(i)}
                  >
                    ×
                  </Button>
                </div>
              ))}
              {candidates.length > 20 && (
                <p className="text-xs text-muted-foreground text-center pt-2">
                  +{candidates.length - 20} more candidates
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {autoDispatch && (
        <div className="space-y-3 border-t pt-4">
          <div>
            <Label className="text-sm font-medium mb-2 block">Email Subject</Label>
            <Input
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              placeholder="Email subject line"
              data-testid="bulk-email-subject"
            />
          </div>
          <div>
            <Label className="text-sm font-medium mb-2 block">
              Email Template
              <span className="text-xs text-muted-foreground ml-2">
                Use {"{{name}}"}, {"{{link}}"}, {"{{expiry}}"} placeholders
              </span>
            </Label>
            <Textarea
              value={emailTemplate}
              onChange={(e) => setEmailTemplate(e.target.value)}
              rows={6}
              className="font-mono text-sm"
              data-testid="bulk-email-template"
            />
          </div>
        </div>
      )}

      {batchStatus && (
        <div className={`p-4 rounded-lg border ${batchStatus.failed > 0 ? "bg-yellow-500/10 border-yellow-500/30" : "bg-green-500/10 border-green-500/30"}`}>
          <div className="flex items-center gap-3">
            {batchStatus.failed > 0 ? (
              <AlertCircle className="w-5 h-5 text-yellow-500" />
            ) : (
              <CheckCircle className="w-5 h-5 text-green-500" />
            )}
            <div>
              <p className="font-medium">Batch #{batchStatus.batchId} processed</p>
              <div className="flex gap-4 text-sm text-muted-foreground">
                <span>Total: {batchStatus.total}</span>
                <span className="text-green-600">Sent: {batchStatus.sent}</span>
                {batchStatus.failed > 0 && (
                  <span className="text-red-600">Failed: {batchStatus.failed}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end gap-3 border-t pt-4">
        <Button
          onClick={handleSubmit}
          disabled={isProcessing || !selectedRoleId || candidates.length === 0}
          className="gap-2"
          data-testid="bulk-submit"
        >
          {isProcessing ? (
            <>
              <Clock className="w-4 h-4 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              {autoDispatch ? "Create & Send Invites" : "Create Batch"}
              {candidates.length > 0 && (
                <Badge variant="secondary" className="ml-1">
                  {candidates.length}
                </Badge>
              )}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
